import { useMemo } from 'react';
import type { ReportClaim, ReportProjection } from '../../services/reportsApi';
import type { TechnicalDetailsModel } from '../AcceptanceProofTechnicalDetails';
import {
  buildCustomerReviewStatusSummary,
  buildCustomerUnprovenSummary,
  buildCustomerWhatHappened,
  buildSafetyReportFollowupFacts,
  CUSTOMER_REVIEW_CHECKLIST,
  deriveCustomerFollowUpFact,
  deriveCustomerOwnerLabel,
  deriveCustomerRuleOwnerLabel,
} from '../acceptanceProofCustomerFacts';
import {
  customerFacingAreaLabel,
  customerFacingSafetyRuleLabel,
  firstStringFromPaths,
  resolveCustomerFacingPatternLabel,
  formatNumber,
  formatTimestamp,
  incidentGroupingKey,
  buildWindowRiskPatternSummary,
} from './workspaceUtils';
import { humanizeZoneLabel } from '../../components/proof/proofDisplayUtils';
import { toIsoFromLocalInput } from '../incidents/useIncidentsTimeRange';
import {
  buildOutcomeBoundarySummary,
  buildScopeBoundarySummary,
} from '../../domain/incidents/sourceContextSemantics';
import { displaySafetyManagerLabel, remapLegacyCustomerFacingCopy } from '../../domain/safetyManagerUxLabels';
import { buildSafetyReportWorkflowProgress } from '../../domain/safetyWorkflowStepMapping';
import {
  buildSafetyReportSecondaryStatusFacts,
  formatCameraCalibrationScopeLabel,
  buildRepresentativeCaseAreaPatternSummary,
  buildSafetyReportControlProgressLabel,
  buildSafetyReportHeadline,
  buildSafetyReportReviewChecklist,
  deriveSafetyReportFollowUpOwnerLabel,
  deriveSafetyReportPatternOwnerLabel,
  followUpOwnerIsAssigned,
  formatSafetyReportOwnerLabel,
  isIncidentCountRiskPatternSummary,
  patternOwnerIsAssigned,
  formatGroupedEventSignalsCopy,
  resolveSafetyReportControlStatusLabel,
  resolveSafetyReportEvidenceProofLabel,
  resolveSafetyReportReadinessState,
  SAFETY_REPORT_MVP_ACTION_SNAPSHOT,
  SAFETY_REPORT_MVP_DEFAULT_LATEST_EVENT,
  SAFETY_REPORT_MVP_EVIDENCE_PACKAGE,
  SAFETY_REPORT_MVP_FOLLOWUP,
  SAFETY_REPORT_MVP_REPORT_SCOPE,
  SAFETY_REPORT_MVP_REVIEW_STATUS,
  SAFETY_REPORT_MVP_RISK_PATTERN,
  SAFETY_REPORT_MVP_UNPROVEN,
  SAFETY_REPORT_MVP_WHAT_HAPPENED,
  SAFETY_REPORT_READINESS_LABELS,
  shouldPreferSafetyReportMvpEvidenceFact,
  shouldUseSafetyReportMvpRollupCopy,
} from './safetyReportMvpPresentation';
import { buildSafetyReportTopStatusPanel, SAFETY_REPORT_ADVANCED_DETAILS_TITLE } from './safetyReportUxCopy';
import { resolveInvestigationAlignmentFromProofPreview } from '../../domain/reportContext/investigationAlignment';
import { resolveClaimsRoiFromProofPreview } from '../../domain/reportContext/claimsRoiAlignment';
import { resolveSafetyControlReviewFromProofPreview } from '../../domain/reportContext/safetyControlReview';

function formatVisibleCount(value: number): string {
  return formatNumber(Math.max(0, Math.floor(Number(value) || 0)));
}

type UseAcceptanceProofViewModelArgs = {
  proofPackPreviewData: any;
  reportScopeIncidents: any[];
  reviewScopeIncidents: any[];
  selectedReviewIncidentIds: string[];
  includedIncidentCaseCount: number | null;
  selectedReviewIncidentOptions: any[];
  currentResolutionProofCase: any;
  explicitProofPolicyId?: string | null;
  caseTotalIncidents?: string | null;
  proofScopedZoneLabel?: string | null;
  proofScopedPatternLabel?: string | null;
  pedestrianZoneHero?: any;
  proofRiskPatternSummary?: any;
  proofTopHotspotLabel?: string | null;
  proofDominantPatternLabel?: string | null;
  hasReadinessDecision: boolean;
  actionRecorded: boolean;
  outcomeRecorded: boolean;
  proofPageState: string;
  evidenceBundleReady: boolean;
  evidenceBundleCreating: boolean;
  evidenceBundleFailed: boolean;
  safetyActionsForUi: any[];
  operationalFollowThroughModel: any;
  supportPackageGeneratedAt?: string | null;
  isLoopedDemoProofCase: boolean;
  anchorIncidentCaseLabel?: string | null;
  hasAppliedIncidentSelection: boolean;
  proofPackExists: boolean;
  proofScopeFacts: Array<{ label: string; value: string }>;
  policyTypeOptions: Array<{ value: string; label: string }>;
  zoneOptions: Array<{ value: string; label: string }>;
  incidentTypeHint?: string | null;
  proofScopePolicyTypeDraft: string;
  proofScopeZoneDraft: string;
  proofScopeFromDraft: string;
  proofScopeToDraft: string;
  reportSiteId?: string | null;
  canonicalProofSiteId?: string | null;
  scopedSiteId?: string | null;
  reportZoneId?: string | null;
  canonicalProofZoneId?: string | null;
  reportDeviceId?: string | null;
  canonicalProofDeviceId?: string | null;
  filterCameraId?: string | null;
  technicalDetailsBackPath: string;
  reviewOutcomeRollup?: any;
  reviewOutcomeRollupLoading?: boolean;
  reviewOutcomeHistory?: any;
  reviewOutcomeHistoryLoading?: boolean;
  ownershipRollup?: any;
  ownershipRollupLoading?: boolean;
  escalationRollup?: any;
  escalationRollupLoading?: boolean;
  correctiveActionRollup?: any;
  correctiveActionRollupLoading?: boolean;
  measurementRollup?: any;
  measurementRollupLoading?: boolean;
  actionSnapshotRollup?: any;
  actionSnapshotRollupLoading?: boolean;
  evidenceConfidenceSummary?: string | null;
};

function reportProjectionFromProofPack(data: any): ReportProjection | null {
  const projection = data?.evidence_pack?.report_projection;
  return projection && typeof projection === 'object' ? (projection as ReportProjection) : null;
}

function reportProjectionClaims(projection: ReportProjection | null | undefined): ReportClaim[] {
  return Array.isArray(projection?.report_claims) ? (projection?.report_claims ?? []) : [];
}

export function useAcceptanceProofViewModel(args: UseAcceptanceProofViewModelArgs) {
  const {
    proofPackPreviewData,
    reportScopeIncidents,
    reviewScopeIncidents,
    selectedReviewIncidentIds,
    includedIncidentCaseCount,
    selectedReviewIncidentOptions,
    currentResolutionProofCase,
    caseTotalIncidents,
    proofScopedZoneLabel,
    proofScopedPatternLabel,
    pedestrianZoneHero,
    proofRiskPatternSummary,
    proofTopHotspotLabel,
    proofDominantPatternLabel,
    hasReadinessDecision,
    actionRecorded,
    outcomeRecorded,
    proofPageState,
    evidenceBundleReady,
    evidenceBundleCreating,
    evidenceBundleFailed,
    safetyActionsForUi,
    operationalFollowThroughModel,
    supportPackageGeneratedAt,
    isLoopedDemoProofCase,
    anchorIncidentCaseLabel,
    hasAppliedIncidentSelection,
    proofPackExists,
    proofScopeFacts,
    policyTypeOptions,
    zoneOptions,
    proofScopePolicyTypeDraft,
    incidentTypeHint = '',
    proofScopeZoneDraft,
    proofScopeFromDraft,
    proofScopeToDraft,
    reportSiteId,
    canonicalProofSiteId,
    scopedSiteId,
    reportZoneId,
    canonicalProofZoneId,
    reportDeviceId,
    canonicalProofDeviceId,
    filterCameraId,
    technicalDetailsBackPath,
    reviewOutcomeRollup,
    reviewOutcomeRollupLoading,
    reviewOutcomeHistory,
    reviewOutcomeHistoryLoading,
    ownershipRollup,
    ownershipRollupLoading,
    escalationRollup,
    escalationRollupLoading,
    correctiveActionRollup,
    correctiveActionRollupLoading,
    measurementRollup,
    measurementRollupLoading,
    actionSnapshotRollup,
    actionSnapshotRollupLoading,
    evidenceConfidenceSummary,
  } = args;

  const reportProjection = useMemo(
    () => reportProjectionFromProofPack(proofPackPreviewData),
    [proofPackPreviewData],
  );

  const reportProjectionClaimList = useMemo(
    () => reportProjectionClaims(reportProjection),
    [reportProjection],
  );

  const reportProjectionCustomerSummary = useMemo(
    () => reportProjection?.customer_summary || {},
    [reportProjection],
  );

  const reportProjectionWorkflowState = useMemo(
    () => reportProjection?.workflow_state || {},
    [reportProjection],
  );
  const finalizedPersistedReport = useMemo(() => {
    const persistedStatus = String(
      proofPackPreviewData?.persisted_report?.status
      || (reportProjection as Record<string, any> | null | undefined)?.report_status
      || '',
    ).trim().toLowerCase();
    return persistedStatus === 'finalized';
  }, [proofPackPreviewData, reportProjection]);

  const reportProjectionEvidenceBreakdown = useMemo(
    () => reportProjection?.evidence_breakdown || {},
    [reportProjection],
  );

  const reportProjectionReportCounts = useMemo(
    () => reportProjection?.report_counts || {},
    [reportProjection],
  );

  const reportProjectionWindowContext = useMemo(
    () => reportProjection?.window_context || {},
    [reportProjection],
  );

  const reportProjectionLabelBundle = useMemo(
    () => reportProjection?.label_bundle || {},
    [reportProjection],
  );

  const reportProjectionSourceContext = useMemo(
    () => reportProjection?.source_context || {},
    [reportProjection],
  );

  const reportProjectionPatternTrace = useMemo(
    () => reportProjection?.pattern_trace || {},
    [reportProjection],
  );
  const investigationAlignment = useMemo(
    () => resolveInvestigationAlignmentFromProofPreview(proofPackPreviewData),
    [proofPackPreviewData],
  );
  const claimsRoiAlignment = useMemo(
    () => resolveClaimsRoiFromProofPreview(proofPackPreviewData),
    [proofPackPreviewData],
  );
  const safetyControlReview = useMemo(
    () => resolveSafetyControlReviewFromProofPreview(proofPackPreviewData),
    [proofPackPreviewData],
  );

  const reportProjectionAnchorPatternSource = useMemo(() => {
    if (reportProjectionPatternTrace && typeof reportProjectionPatternTrace === 'object' && Object.keys(reportProjectionPatternTrace).length > 0) {
      return reportProjectionPatternTrace as Record<string, any>;
    }
    const traceSource = reportProjection?.technical_trace?.anchor_pattern_source;
    if (traceSource && typeof traceSource === 'object') return traceSource as Record<string, any>;
    const patternClaim = reportProjectionClaimList.find((claim) => claim?.claim_scope_type === 'pattern_claim');
    if (patternClaim?.pattern_source && typeof patternClaim.pattern_source === 'object') {
      return patternClaim.pattern_source as Record<string, any>;
    }
    return null;
  }, [reportProjection, reportProjectionClaimList, reportProjectionPatternTrace]);

  const customerOwnerLabel = useMemo(() => {
    const projectedOwner = String(
      reportProjectionWorkflowState?.ownership?.followup_owner
      || reportProjectionWorkflowState?.ownership?.owner
      || '',
    ).trim();
    if (projectedOwner) return projectedOwner;
    return deriveCustomerOwnerLabel(reportScopeIncidents);
  }, [reportProjectionWorkflowState, reportScopeIncidents]);

  const customerRuleOwnerLabel = useMemo(() => {
    const projectedOwner = String(reportProjectionWorkflowState?.ownership?.rule_owner || '').trim();
    if (projectedOwner) return projectedOwner;
    return deriveCustomerRuleOwnerLabel(reportScopeIncidents);
  }, [reportProjectionWorkflowState, reportScopeIncidents]);

  const incidentGroupItems = useMemo(() => {
    const items = proofPackPreviewData?.evidence_pack?.contents?.incident_groups?.items;
    return Array.isArray(items) ? items : [];
  }, [proofPackPreviewData]);

  const selectedIncidentCountForCustomer = useMemo(() => {
    const projected = Number(
      reportProjectionReportCounts?.selected_incidents
      ?? reportProjectionWindowContext?.included_incident_cases
      ?? NaN,
    );
    if (Number.isFinite(projected) && projected > 0) return projected;
    if (selectedReviewIncidentIds.length > 0) return selectedReviewIncidentIds.length;
    if (includedIncidentCaseCount != null) return includedIncidentCaseCount;
    if (reportScopeIncidents.length > 0) return 1;
    return null;
  }, [includedIncidentCaseCount, reportProjectionReportCounts, reportProjectionWindowContext, reportScopeIncidents.length, selectedReviewIncidentIds.length]);

  const relatedAlertEventCount = useMemo(() => {
    const projected = Number(
      reportProjectionReportCounts?.related_alerts_events
      ?? reportProjectionWindowContext?.recent_recurrence_alert_count
      ?? NaN,
    );
    if (Number.isFinite(projected) && projected > 0) return projected;
    if (!incidentGroupItems.length || !reportScopeIncidents.length) return null;

    const incidentIds = new Set(
      reportScopeIncidents
        .map((incident) => String(firstStringFromPaths(incident, ['incident_id', 'id']) || '').trim())
        .filter(Boolean),
    );
    const groupKeys = new Set(
      reportScopeIncidents
        .map((incident) => String(firstStringFromPaths(incident, ['group_key', 'groupKey']) || '').trim())
        .filter(Boolean),
    );
    const groupingKeys = new Set(
      reportScopeIncidents
        .map((incident) => incidentGroupingKey(incident))
        .filter((key) => key !== '|||'),
    );

    const matched = new Map<string, any>();
    incidentGroupItems.forEach((item: any) => {
      const itemIncidentId = String(firstStringFromPaths(item, ['incident_id', 'id']) || '').trim();
      const itemGroupKey = String(firstStringFromPaths(item, ['group_key', 'groupKey']) || '').trim();
      const itemGroupingKey = incidentGroupingKey(item);
      const matches = (
        (itemIncidentId && incidentIds.has(itemIncidentId))
        || (itemGroupKey && groupKeys.has(itemGroupKey))
        || (itemGroupingKey !== '|||' && groupingKeys.has(itemGroupingKey))
      );
      if (!matches) return;
      const dedupeKey = itemGroupKey || itemIncidentId || itemGroupingKey;
      if (!matched.has(dedupeKey)) matched.set(dedupeKey, item);
    });

    if (!matched.size) return null;
    const total = Array.from(matched.values()).reduce((sum, item: any) => {
      const count = Number(
        item?.recent_recurrence_alert_count
        ?? item?.recent_alert_count
        ?? item?.selected_window_alert_count
        ?? item?.alert_count,
      );
      return sum + (Number.isFinite(count) ? count : 0);
    }, 0);
    return total > 0 ? total : null;
  }, [incidentGroupItems, reportProjectionReportCounts, reportProjectionWindowContext, reportScopeIncidents]);

  const evidenceIndexEntries = useMemo(() => {
    const evidenceIndex = proofPackPreviewData?.case_file?.evidence_index;
    return Array.isArray(evidenceIndex) ? evidenceIndex : [];
  }, [proofPackPreviewData]);

  const latestTimelineEvent = useMemo(() => {
    const timeline = proofPackPreviewData?.case_file?.timeline;
    return Array.isArray(timeline)
      ? timeline.find((item: any) => item && typeof item === 'object')
      : undefined;
  }, [proofPackPreviewData]);

  const snapshotReadyCount = useMemo(
    () => evidenceIndexEntries.filter((entry: any) => String(entry?.kind || '').toUpperCase() === 'SNAPSHOT' && ['AVAILABLE', 'READY'].includes(String(entry?.status || '').toUpperCase())).length,
    [evidenceIndexEntries],
  );

  const clipReadyCount = useMemo(
    () => evidenceIndexEntries.filter((entry: any) => String(entry?.kind || '').toUpperCase() === 'CLIP' && ['AVAILABLE', 'READY'].includes(String(entry?.status || '').toUpperCase())).length,
    [evidenceIndexEntries],
  );

  const clipPendingCount = useMemo(
    () => evidenceIndexEntries.filter((entry: any) => String(entry?.kind || '').toUpperCase() === 'CLIP' && String(entry?.status || '').toUpperCase() === 'PENDING').length,
    [evidenceIndexEntries],
  );

  const latestEventTimestampLabel = useMemo(
    () => formatTimestamp(String(latestTimelineEvent?.ts || currentResolutionProofCase.timestamps.observedAt || '').trim()) || 'Not captured yet',
    [currentResolutionProofCase.timestamps.observedAt, latestTimelineEvent?.ts],
  );

  const customerAreaLabel = useMemo(() => {
    const raw = String(
      reportProjectionLabelBundle?.customer_area
      || pedestrianZoneHero?.areaLabel
      || proofScopedZoneLabel
      || currentResolutionProofCase.zone
      || '',
    ).trim();
    return customerFacingAreaLabel(raw) || 'the selected operating area';
  }, [currentResolutionProofCase.zone, pedestrianZoneHero?.areaLabel, proofScopedZoneLabel, reportProjectionLabelBundle]);

  const customerSafetyRuleLabel = useMemo(() => {
    if (pedestrianZoneHero) return customerFacingSafetyRuleLabel('Pedestrian in forklift area');
    const raw = String(
      reportProjectionLabelBundle?.customer_rule
      || proofScopedPatternLabel
      || currentResolutionProofCase.pattern
      || 'safety event',
    ).trim();
    return customerFacingSafetyRuleLabel(raw);
  }, [currentResolutionProofCase.pattern, pedestrianZoneHero, proofScopedPatternLabel, reportProjectionLabelBundle]);

  const customerIncidentLabel = useMemo(() => {
    if (customerSafetyRuleLabel === 'Pedestrian Activity in Forklift Zone') return 'pedestrian activity observation';
    return customerSafetyRuleLabel.toLowerCase();
  }, [customerSafetyRuleLabel]);

  const projectionFollowupFact = useMemo(() => {
    const measurementLabel = remapLegacyCustomerFacingCopy(String(reportProjectionWorkflowState?.outcome_measurement?.state_label || '').trim());
    if (measurementLabel && /measured/i.test(measurementLabel)) return measurementLabel;
    const actionLabel = remapLegacyCustomerFacingCopy(String(reportProjectionWorkflowState?.action_snapshot?.state_label || '').trim());
    if (actionLabel) return actionLabel;
    const correctiveActionLabel = remapLegacyCustomerFacingCopy(String(reportProjectionWorkflowState?.corrective_action?.state_label || '').trim());
    if (correctiveActionLabel && !/no work order yet/i.test(correctiveActionLabel)) return correctiveActionLabel;
    for (const claim of reportProjectionClaimList) {
      const claimType = String(claim?.claim_type || '').trim();
      if (claimType === 'outcome_measured') return 'Outcome measured';
      if (claimType === 'followup_scheduled') return 'Action scheduled';
      if (claimType === 'action_logged') return 'Action logged';
    }
    return '';
  }, [reportProjectionClaimList, reportProjectionWorkflowState]);

  const projectionCurrentActionLabel = useMemo(() => {
    const actionLabel = remapLegacyCustomerFacingCopy(String(reportProjectionWorkflowState?.action_snapshot?.state_label || '').trim());
    if (actionLabel) return actionLabel;
    const correctiveActionLabel = remapLegacyCustomerFacingCopy(String(reportProjectionWorkflowState?.corrective_action?.state_label || '').trim());
    if (correctiveActionLabel) return correctiveActionLabel;
    for (const claim of reportProjectionClaimList) {
      const claimType = String(claim?.claim_type || '').trim();
      if (claimType === 'outcome_measured') return 'Outcome measured';
      if (claimType === 'followup_scheduled') return 'Action scheduled';
      if (claimType === 'action_logged') return 'Action logged';
    }
    return '';
  }, [reportProjectionClaimList, reportProjectionWorkflowState]);

  const projectionReviewed = useMemo(
    () => reportProjectionClaimList.some((claim) => String(claim?.claim_type || '').trim() === 'incident_reviewed'),
    [reportProjectionClaimList],
  );

  const hasSelectedSafetyEvent = useMemo(
    () => selectedReviewIncidentIds.length > 0 || reportScopeIncidents.length > 0,
    [reportScopeIncidents.length, selectedReviewIncidentIds.length],
  );
  const reviewComplete = projectionReviewed;
  const impactTracked = outcomeRecorded;

  const safetyReportReadinessState = useMemo(
    () => resolveSafetyReportReadinessState({
      hasSelectedSafetyEvent,
      hasCapturedEvidence: hasSelectedSafetyEvent && proofPageState !== 'GENERATING',
      reviewComplete,
      ownerAssigned:
        customerOwnerLabel !== 'Not assigned in Review yet'
        && customerOwnerLabel !== 'Not assigned',
      correctiveLinked: Boolean(
        projectionCurrentActionLabel
        && !/pending|not linked|not assigned|create follow-up/i.test(String(projectionCurrentActionLabel || '')),
      ),
      followUpMeasured: impactTracked,
    }),
    [
      customerOwnerLabel,
      hasSelectedSafetyEvent,
      impactTracked,
      projectionCurrentActionLabel,
      reviewComplete,
      proofPageState,
    ],
  );

  const safetyReportReadinessLabel = SAFETY_REPORT_READINESS_LABELS[safetyReportReadinessState];

  const safetyReportMvpPresentationActive = useMemo(
    () => hasSelectedSafetyEvent && !reviewComplete,
    [hasSelectedSafetyEvent, reviewComplete],
  );

  const rollupScopeTotal = useMemo(
    () => Math.max(
      Number(reviewOutcomeRollup?.total_incidents || 0),
      Number(actionSnapshotRollup?.total_incidents || 0),
      Number(ownershipRollup?.total_incidents || 0),
    ),
    [actionSnapshotRollup?.total_incidents, ownershipRollup?.total_incidents, reviewOutcomeRollup?.total_incidents],
  );

  const rollupScopeLoading = useMemo(
    () =>
      reviewOutcomeRollupLoading
      || ownershipRollupLoading
      || escalationRollupLoading
      || correctiveActionRollupLoading
      || measurementRollupLoading
      || actionSnapshotRollupLoading,
    [
      actionSnapshotRollupLoading,
      correctiveActionRollupLoading,
      escalationRollupLoading,
      measurementRollupLoading,
      ownershipRollupLoading,
      reviewOutcomeRollupLoading,
    ],
  );

  const safetyReportMvpRollupActive = useMemo(() => {
    const inflatedScope = rollupScopeTotal > 10;
    if (hasReadinessDecision && !rollupScopeLoading && !inflatedScope) return false;
    return shouldUseSafetyReportMvpRollupCopy({
      loading: rollupScopeLoading,
      totalIncidents: rollupScopeTotal,
      reviewComplete,
    });
  }, [
    hasReadinessDecision,
    reviewComplete,
    rollupScopeLoading,
    rollupScopeTotal,
  ]);

  const customerFacingPatternLabel = useMemo(
    () => resolveCustomerFacingPatternLabel({
      preferredLabel: proofScopedPatternLabel || proofDominantPatternLabel,
      policyTypeId: proofScopePolicyTypeDraft,
      incidents: reportScopeIncidents,
      policyTypeOptions,
    }),
    [
      policyTypeOptions,
      proofDominantPatternLabel,
      proofScopePolicyTypeDraft,
      proofScopedPatternLabel,
      reportScopeIncidents,
    ],
  );

  const customerStatusLabel = useMemo(() => {
    if (finalizedPersistedReport) return 'Signed off';
    const projected = String(
      reportProjectionWorkflowState?.safety_case_state_label
      || reportProjectionWorkflowState?.review?.status_label
      || '',
    ).trim();
    if (projected) return projected;
    if (proofPageState === 'ERROR') return 'Report unavailable';
    if (outcomeRecorded) return 'Impact tracking recorded';
    if (safetyReportReadinessState === 'requires_review') return 'Control not verified';
    if (safetyReportReadinessState === 'ready') return SAFETY_REPORT_READINESS_LABELS.ready;
    if (safetyReportReadinessState === 'blocked') return SAFETY_REPORT_READINESS_LABELS.blocked;
    return safetyReportReadinessLabel;
  }, [finalizedPersistedReport, outcomeRecorded, proofPageState, reportProjectionWorkflowState, safetyReportReadinessLabel, safetyReportReadinessState]);

  const customerEvidenceFact = useMemo(() => {
    const projectedClips = Number(reportProjectionEvidenceBreakdown?.accessible_clips || 0);
    const projectedSnapshots = Number(reportProjectionEvidenceBreakdown?.accessible_snapshots || 0);
    const projectedClipStatus = String(reportProjectionEvidenceBreakdown?.clip_status || '').trim().toUpperCase();
    const projectedReadiness = String(reportProjectionEvidenceBreakdown?.readiness_label || '').trim();
    if (projectedClips > 0) return `${formatVisibleCount(projectedClips)} video clip${projectedClips === 1 ? '' : 's'} available`;
    if (projectedClipStatus === 'PENDING') return 'Video clip still processing';
    if (projectedSnapshots > 0) return `${formatVisibleCount(projectedSnapshots)} snapshot${projectedSnapshots === 1 ? '' : 's'} available`;
    if (projectedReadiness === 'Ready with limitations') return projectedReadiness;
    if (projectedReadiness) return `${projectedReadiness} evidence readiness`;
    if (clipReadyCount > 0) return `${formatVisibleCount(clipReadyCount)} video clip${clipReadyCount === 1 ? '' : 's'} available`;
    if (clipPendingCount > 0) return `${formatVisibleCount(clipPendingCount)} video clip${clipPendingCount === 1 ? '' : 's'} still processing`;
    if (snapshotReadyCount > 0) return `${formatVisibleCount(snapshotReadyCount)} snapshot${snapshotReadyCount === 1 ? '' : 's'} available`;
    if (proofPageState === 'GENERATING') return 'Evidence still being prepared';
    if (
      safetyReportMvpPresentationActive
      && shouldPreferSafetyReportMvpEvidenceFact({
        hasSelectedSafetyEvent,
        reviewComplete,
      })
    ) {
      return resolveSafetyReportEvidenceProofLabel({});
    }
    return 'Evidence not yet available';
  }, [
    clipPendingCount,
    clipReadyCount,
    hasSelectedSafetyEvent,
    reviewComplete,
    safetyReportMvpPresentationActive,
    proofPageState,
    reportProjectionEvidenceBreakdown,
    snapshotReadyCount,
  ]);

  const customerFollowUpFact = useMemo(() => {
    if (projectionFollowupFact) return projectionFollowupFact;
    if (safetyReportMvpPresentationActive && safetyReportMvpRollupActive) {
      return 'Not assigned';
    }
    return deriveCustomerFollowUpFact({
      reportScopeIncidents,
      actionRecorded,
      outcomeRecorded,
    });
  }, [actionRecorded, outcomeRecorded, projectionFollowupFact, reportScopeIncidents, safetyReportMvpPresentationActive, safetyReportMvpRollupActive]);

  const customerLatestEventLabel = useMemo(() => {
    const captured = String(latestEventTimestampLabel || '').trim();
    if (captured && captured !== 'Not captured yet' && captured !== 'Not available yet') return captured;
    if (safetyReportMvpPresentationActive) return SAFETY_REPORT_MVP_DEFAULT_LATEST_EVENT;
    return captured || 'Not captured yet';
  }, [latestEventTimestampLabel, safetyReportMvpPresentationActive]);

  const customerPatternOwnerLabel = useMemo(() => {
    const fromScope = deriveSafetyReportPatternOwnerLabel(reportScopeIncidents);
    if (fromScope !== 'Not assigned') return fromScope;
    const selectedIncidents = selectedReviewIncidentOptions
      .map((option) => option.rawIncident)
      .filter(Boolean) as Array<Record<string, unknown>>;
    const fromSelected = deriveSafetyReportPatternOwnerLabel(selectedIncidents);
    if (fromSelected !== 'Not assigned') return fromSelected;
    const ownership = reportProjectionWorkflowState?.ownership as Record<string, unknown> | undefined;
    const projectedPatternOwner = String(
      ownership?.pattern_owner || '',
    ).trim();
    if (projectedPatternOwner) return projectedPatternOwner;
    return 'Not assigned';
  }, [reportProjectionWorkflowState, reportScopeIncidents, selectedReviewIncidentOptions]);

  const customerFollowUpOwnerLabel = useMemo(
    () => deriveSafetyReportFollowUpOwnerLabel(
      reportScopeIncidents,
      reportProjectionWorkflowState?.ownership?.followup_owner
        || reportProjectionWorkflowState?.ownership?.owner,
    ),
    [reportProjectionWorkflowState, reportScopeIncidents],
  );

  const customerPatternOwnerDisplayLabel = useMemo(() => {
    if (proofPageState === 'GENERATING' && !patternOwnerIsAssigned(customerPatternOwnerLabel)) {
      return 'Resolving ownership...';
    }
    return customerPatternOwnerLabel;
  }, [customerPatternOwnerLabel, proofPageState]);

  const customerFollowUpOwnerDisplayLabel = useMemo(() => {
    if (proofPageState === 'GENERATING' && !followUpOwnerIsAssigned(customerFollowUpOwnerLabel)) {
      return 'Resolving ownership...';
    }
    return customerFollowUpOwnerLabel;
  }, [customerFollowUpOwnerLabel, proofPageState]);

  const safetyWorkflowProgress = useMemo(
    () => buildSafetyReportWorkflowProgress({
      hasSelectedSafetyEvent,
      projectionReviewed,
      outcomeRecorded,
      patternOwnerLabel: customerPatternOwnerDisplayLabel,
      followUpOwnerLabel: customerFollowUpOwnerDisplayLabel,
      projectionCurrentActionLabel,
      proofPageState,
      safetyReportReadinessState,
    }),
    [
      customerFollowUpOwnerDisplayLabel,
      customerPatternOwnerDisplayLabel,
      hasSelectedSafetyEvent,
      outcomeRecorded,
      projectionCurrentActionLabel,
      projectionReviewed,
      proofPageState,
      safetyReportReadinessState,
    ],
  );

  const customerSummarySentence = useMemo(() => {
    const projected = remapLegacyCustomerFacingCopy(String(reportProjectionCustomerSummary?.summary_sentence || '').trim());
    if (projected) return projected;
    return buildSafetyReportHeadline({
      patternOwnerAssigned: patternOwnerIsAssigned(customerPatternOwnerLabel),
      followUpOwnerAssigned: followUpOwnerIsAssigned(customerFollowUpOwnerLabel),
    });
  }, [
    customerFollowUpOwnerLabel,
    customerPatternOwnerLabel,
    reportProjectionCustomerSummary,
  ]);

  const customerControlStatusLabel = useMemo(() => {
    if (finalizedPersistedReport) return 'Signed off';
    if (proofPageState === 'ERROR') return 'Report unavailable';
    return resolveSafetyReportControlStatusLabel({
      controlStatusKey: String(reportProjectionWorkflowState?.safety_case_state || '').trim() || 'exposed',
      apiLabel: reportProjectionWorkflowState?.safety_case_state_label,
      reviewComplete,
      outcomeRecorded: impactTracked,
    });
  }, [finalizedPersistedReport, impactTracked, proofPageState, reportProjectionWorkflowState, reviewComplete]);

  const customerReviewStatusLabel = useMemo(() => {
    if (!projectionReviewed) return 'Pending review';
    return '';
  }, [projectionReviewed]);

  const customerEvidenceProofLabel = useMemo(() => {
    const projectedReadiness = String(reportProjectionEvidenceBreakdown?.readiness_label || '').trim();
    if (projectedReadiness || clipPendingCount > 0 || proofPageState === 'GENERATING') {
      return resolveSafetyReportEvidenceProofLabel({
        readinessLabel: projectedReadiness,
        clipPending: clipPendingCount > 0,
        generating: proofPageState === 'GENERATING',
      });
    }
    if (customerEvidenceFact.includes('limitations') || customerEvidenceFact.includes('Available')) {
      return 'Limited — review required';
    }
    if (customerEvidenceFact.includes('processing') || customerEvidenceFact.includes('prepared')) {
      return 'Pending';
    }
    if (customerEvidenceFact.includes('not yet') || customerEvidenceFact.includes('not available')) {
      return 'Not evaluated';
    }
    return resolveSafetyReportEvidenceProofLabel({ readinessLabel: customerEvidenceFact });
  }, [
    clipPendingCount,
    customerEvidenceFact,
    proofPageState,
    reportProjectionEvidenceBreakdown?.readiness_label,
  ]);

  const customerCameraCalibrationLabel = useMemo(() => {
    const incident = reportScopeIncidents[0] as Record<string, unknown> | undefined;
    const safetyCase = incident?.safety_case as Record<string, unknown> | undefined;
    return formatCameraCalibrationScopeLabel(
      safetyCase?.calibration_artifact as Record<string, unknown> | undefined,
      safetyCase?.evidence_quality as Record<string, unknown> | undefined,
    );
  }, [reportScopeIncidents]);

  const customerTopStatusPanel = useMemo(
    () => buildSafetyReportTopStatusPanel({
      reviewComplete: projectionReviewed,
      controlStatusLabel: customerControlStatusLabel,
      nextStepLabel: safetyWorkflowProgress.nextActionLabel,
      patternOwnerAssigned: patternOwnerIsAssigned(customerPatternOwnerLabel),
      followUpOwnerAssigned: followUpOwnerIsAssigned(customerFollowUpOwnerLabel),
      impactTracked: impactTracked,
      evidenceStrengthLabel: customerEvidenceProofLabel,
      reportFinalized: finalizedPersistedReport,
    }),
    [
      customerControlStatusLabel,
      customerEvidenceProofLabel,
      customerFollowUpOwnerLabel,
      customerPatternOwnerLabel,
      finalizedPersistedReport,
      impactTracked,
      projectionReviewed,
      safetyWorkflowProgress.nextActionLabel,
    ],
  );

  const customerSecondaryStatusFacts = useMemo(
    () => buildSafetyReportSecondaryStatusFacts({
      reviewStatusLabel: customerReviewStatusLabel,
      evidenceProofLabel: customerEvidenceProofLabel,
      latestEventLabel: customerLatestEventLabel,
      followUpOwnerLabel: customerFollowUpOwnerDisplayLabel,
      controlProgressLabel: buildSafetyReportControlProgressLabel({
        patternOwnerAssigned: patternOwnerIsAssigned(customerPatternOwnerLabel),
        followUpOwnerAssigned: followUpOwnerIsAssigned(customerFollowUpOwnerLabel),
      }),
      cameraCalibrationLabel: customerCameraCalibrationLabel,
    }),
    [
      customerCameraCalibrationLabel,
      customerEvidenceProofLabel,
      customerFollowUpOwnerDisplayLabel,
      customerFollowUpOwnerLabel,
      customerLatestEventLabel,
      customerPatternOwnerLabel,
      customerReviewStatusLabel,
    ],
  );

  const customerWhatHappened = useMemo(() => {
    const projected = remapLegacyCustomerFacingCopy(String(reportProjectionCustomerSummary?.what_happened_text || '').trim());
    if (projected) return projected;
    if (safetyReportMvpPresentationActive) return SAFETY_REPORT_MVP_WHAT_HAPPENED;
    return buildCustomerWhatHappened({
      pedestrianZoneHero,
      customerSafetyRuleLabel,
      customerAreaLabel,
      customerIncidentLabel,
    });
  }, [customerAreaLabel, customerIncidentLabel, customerSafetyRuleLabel, pedestrianZoneHero, reportProjectionCustomerSummary, safetyReportMvpPresentationActive]);

  const fallbackRiskPatternSummary = useMemo(() => {
    if (proofRiskPatternSummary) return null;
    const selectedIncidents = selectedReviewIncidentOptions
      .map((option) => option.rawIncident)
      .filter(Boolean);
    if (selectedIncidents.length > 0) {
      return buildWindowRiskPatternSummary(selectedIncidents);
    }
    if (reportScopeIncidents.length > 0) {
      return buildWindowRiskPatternSummary(reportScopeIncidents);
    }
    return buildWindowRiskPatternSummary(reviewScopeIncidents);
  }, [proofRiskPatternSummary, reportScopeIncidents, reviewScopeIncidents, selectedReviewIncidentOptions]);

  const visibleRiskPatternSummary = useMemo(
    () => proofRiskPatternSummary || fallbackRiskPatternSummary,
    [fallbackRiskPatternSummary, proofRiskPatternSummary],
  );

  const visibleRiskPatternTopHotspotLabel = useMemo(() => {
    if (proofTopHotspotLabel) return proofTopHotspotLabel;
    const raw = String(visibleRiskPatternSummary?.top_hotspot || '').trim();
    if (!raw) return '';
    const human = humanizeZoneLabel(raw);
    return customerFacingAreaLabel(human || raw) || human || raw;
  }, [proofTopHotspotLabel, visibleRiskPatternSummary?.top_hotspot]);

  const visibleRiskPatternDominantLabel = useMemo(() => {
    if (customerFacingPatternLabel && customerFacingPatternLabel !== 'Safety event') {
      return customerFacingPatternLabel;
    }
    if (proofDominantPatternLabel) {
      return resolveCustomerFacingPatternLabel({
        preferredLabel: proofDominantPatternLabel,
        policyTypeId: proofScopePolicyTypeDraft,
        incidents: reportScopeIncidents,
        policyTypeOptions,
      });
    }
    const raw = String(visibleRiskPatternSummary?.strongest_pattern_label || '').trim();
    if (!raw) return '';
    return resolveCustomerFacingPatternLabel({
      preferredLabel: raw,
      policyTypeId: proofScopePolicyTypeDraft,
      incidents: reportScopeIncidents,
      policyTypeOptions,
    });
  }, [
    customerFacingPatternLabel,
    policyTypeOptions,
    proofDominantPatternLabel,
    proofScopePolicyTypeDraft,
    reportScopeIncidents,
    visibleRiskPatternSummary?.strongest_pattern_label,
  ]);

  const customerRiskPatternSummary = useMemo(() => {
    const projectedRecurrenceNote = String(reportProjectionPatternTrace?.recurrence_note || '').trim();
    if (projectedRecurrenceNote) return projectedRecurrenceNote;
    const representativeCaseCount = selectedReviewIncidentIds.length > 0
      ? selectedReviewIncidentIds.length
      : (hasSelectedSafetyEvent ? 1 : 0);
    if (representativeCaseCount === 1) {
      const areaLabel = visibleRiskPatternTopHotspotLabel
        || customerFacingAreaLabel(customerAreaLabel)
        || customerAreaLabel;
      if (areaLabel && areaLabel !== 'Area not assigned') {
        return buildRepresentativeCaseAreaPatternSummary(areaLabel);
      }
    }
    const summaryLine = String(visibleRiskPatternSummary?.summary || '').trim();
    if (summaryLine && !(representativeCaseCount === 1 && isIncidentCountRiskPatternSummary(summaryLine))) {
      return summaryLine.replace(/this proof window/gi, 'this reporting period');
    }
    if (visibleRiskPatternDominantLabel && visibleRiskPatternTopHotspotLabel) {
      return `${visibleRiskPatternDominantLabel} is most concentrated in ${visibleRiskPatternTopHotspotLabel} for this report window.`;
    }
    if (visibleRiskPatternTopHotspotLabel) {
      return `Risk is most concentrated in ${visibleRiskPatternTopHotspotLabel} for this report window.`;
    }
    if (visibleRiskPatternDominantLabel) {
      return `${visibleRiskPatternDominantLabel} is the main recurring risk in this report window.`;
    }
    if (safetyReportMvpRollupActive && !projectedRecurrenceNote && !summaryLine) {
      return SAFETY_REPORT_MVP_RISK_PATTERN;
    }
    if (!hasReadinessDecision) {
      return 'Recurring-risk summary will appear after the Safety Report scope is confirmed for this case.';
    }
    return 'This report window does not yet show a durable recurring risk.';
  }, [
    customerAreaLabel,
    hasReadinessDecision,
    hasSelectedSafetyEvent,
    reportProjectionPatternTrace?.recurrence_note,
    safetyReportMvpRollupActive,
    selectedReviewIncidentIds.length,
    visibleRiskPatternDominantLabel,
    visibleRiskPatternSummary?.summary,
    visibleRiskPatternTopHotspotLabel,
  ]);

  const customerRiskPatternFacts = useMemo(() => {
    const rows: Array<{ label: string; value: string }> = [];
    const stateLabel = String(visibleRiskPatternSummary?.label || '').trim();
    const projectedStateLabel = String(reportProjectionPatternTrace?.pattern_label || stateLabel).trim();
    if (projectedStateLabel) rows.push({ label: 'Risk state', value: projectedStateLabel });
    if (visibleRiskPatternTopHotspotLabel) rows.push({ label: 'Top area', value: visibleRiskPatternTopHotspotLabel });
    if (visibleRiskPatternDominantLabel) {
      rows.push({ label: 'Main recurring risk', value: visibleRiskPatternDominantLabel });
    }
    const patternSourceType = String(reportProjectionAnchorPatternSource?.source_type || '').trim();
    if (patternSourceType) {
      rows.push({
        label: 'Risk source',
        value: /^incident_group$/i.test(patternSourceType) ? 'Recurrence analysis' : patternSourceType.replace(/_/g, ' '),
      });
    }
    const concentration = Number(visibleRiskPatternSummary?.concentration_percent || 0);
    if (Number.isFinite(concentration) && concentration > 0) {
      rows.push({ label: 'Area concentration', value: `${concentration}% of this report window` });
    }
    const repeatCount = Number(visibleRiskPatternSummary?.repeat_pattern_count || 0);
    if (Number.isFinite(repeatCount) && repeatCount > 0) {
      rows.push({ label: 'Repeat signals', value: `${formatVisibleCount(repeatCount)} recurring signal${repeatCount === 1 ? '' : 's'}` });
    }
    const lookbackSignals = Number(reportProjectionPatternTrace?.lookback_signal_count ?? reportProjectionAnchorPatternSource?.lookback_signal_count ?? 0);
    if (Number.isFinite(lookbackSignals) && lookbackSignals > 0) {
      rows.push({ label: 'Lookback signals', value: formatVisibleCount(lookbackSignals) });
    }
    const selectedSignals = Number(reportProjectionPatternTrace?.selected_window_alert_count || 0);
    if (Number.isFinite(selectedSignals) && selectedSignals > 0) {
      rows.push({ label: 'Event signals in scope', value: formatVisibleCount(selectedSignals) });
    }
    return rows;
  }, [
    reportProjectionAnchorPatternSource,
    reportProjectionPatternTrace?.lookback_signal_count,
    reportProjectionPatternTrace?.pattern_label,
    reportProjectionPatternTrace?.selected_window_alert_count,
    visibleRiskPatternDominantLabel,
    visibleRiskPatternSummary?.concentration_percent,
    visibleRiskPatternSummary?.label,
    visibleRiskPatternSummary?.repeat_pattern_count,
    visibleRiskPatternTopHotspotLabel,
  ]);

  const customerEvidencePoints = useMemo(() => {
    const projectedRelated = Number(reportProjectionReportCounts?.related_alerts_events || 0);
    if (safetyReportMvpRollupActive && projectedRelated <= 0 && relatedAlertEventCount == null) {
      return [...SAFETY_REPORT_MVP_EVIDENCE_PACKAGE];
    }
    const signalCount = relatedAlertEventCount != null
      ? Number(relatedAlertEventCount)
      : (projectedRelated > 0 ? projectedRelated : 0);
    const groupedSignalsLine = signalCount > 0
      ? formatGroupedEventSignalsCopy(signalCount).replace('this recurring risk pattern', 'one recurring risk pattern')
      : 'Camera observation grouping for this recurring risk pattern is not recorded in this report yet';
    const representativeCount = Math.max(1, Number(selectedIncidentCountForCustomer) || 1);
    const representativeLine = representativeCount === 1
      ? '1 representative evidence sample selected'
      : `${formatVisibleCount(representativeCount)} representative evidence samples selected`;
    return [
      representativeLine,
      groupedSignalsLine,
      'Event timeline available',
      'Detection and review details available',
      clipReadyCount > 0
        ? `${formatVisibleCount(clipReadyCount)} video clip${clipReadyCount === 1 ? '' : 's'} available`
        : 'Video clip pending',
      snapshotReadyCount > 0
        ? `${formatVisibleCount(snapshotReadyCount)} snapshot${snapshotReadyCount === 1 ? '' : 's'} available`
        : 'Snapshot pending',
    ];
  }, [
    clipReadyCount,
    relatedAlertEventCount,
    reportProjectionReportCounts?.related_alerts_events,
    safetyReportMvpRollupActive,
    selectedIncidentCountForCustomer,
    snapshotReadyCount,
  ]);

  const customerReviewStatusSummary = useMemo(() => {
    const projected = remapLegacyCustomerFacingCopy(String(reportProjectionCustomerSummary?.review_status_text || '').trim());
    if (projected) {
      const reviewedBy = String(reportProjectionWorkflowState?.review?.reviewed_by || '').trim();
      const reviewedAt = formatTimestamp(String(reportProjectionWorkflowState?.review?.reviewed_at || '').trim());
      if (reviewedBy && reviewedAt) return `${projected} Reviewed by ${reviewedBy} on ${reviewedAt}.`;
      if (reviewedBy) return `${projected} Reviewed by ${reviewedBy}.`;
      if (reviewedAt) return `${projected} Recorded on ${reviewedAt}.`;
      return projected;
    }
    if (safetyReportMvpRollupActive) {
      return SAFETY_REPORT_MVP_REVIEW_STATUS;
    }
    return buildCustomerReviewStatusSummary(proofPageState, outcomeRecorded);
  }, [
    outcomeRecorded,
    proofPageState,
    reportProjectionCustomerSummary,
    reportProjectionWorkflowState,
    safetyReportMvpRollupActive,
  ]);

  const customerReviewChecklist = useMemo(
    () => (
      projectionReviewed
        ? []
        : (safetyReportMvpRollupActive
          ? buildSafetyReportReviewChecklist(patternOwnerIsAssigned(customerPatternOwnerLabel))
          : CUSTOMER_REVIEW_CHECKLIST)
    ),
    [customerPatternOwnerLabel, projectionReviewed, safetyReportMvpRollupActive],
  );

  const customerReviewOutcomeRows = useMemo(
    () => (
      Array.isArray(reviewOutcomeRollup?.items)
        ? (reviewOutcomeRollup?.items ?? []).filter((item: any) => Number(item?.count || 0) > 0).slice(0, 4)
        : []
    ),
    [reviewOutcomeRollup],
  );

  const customerReviewOutcomeSummary = useMemo(() => {
    if (safetyReportMvpRollupActive) return SAFETY_REPORT_MVP_REPORT_SCOPE.reviewOutcome;
    if (reviewOutcomeRollupLoading) return 'Review status for this report scope is still being assembled.';
    const rollup = reviewOutcomeRollup;
    const total = Number(rollup?.total_incidents || 0);
    const reviewed = Number(rollup?.reviewed_total || 0);
    const confirmedRisk = Number(rollup?.customer_risk_total || 0);
    const uncertainRisk = Number(
      Array.isArray(rollup?.risk_disposition_items)
        ? ((rollup.risk_disposition_items.find((item: any) => String(item?.risk_disposition || '') === 'uncertain_risk')?.count) || 0)
        : 0,
    );
    const excludedRisk = Number(
      Array.isArray(rollup?.risk_disposition_items)
        ? ((rollup.risk_disposition_items.find((item: any) => String(item?.risk_disposition || '') === 'excluded_from_risk')?.count) || 0)
        : 0,
    );
    if (total <= 0) return 'No evidence records are in scope for this report window yet.';
    if (reviewed <= 0) return `${formatVisibleCount(total)} evidence records are in scope, but none have a recorded review status yet.`;
    return `${formatVisibleCount(reviewed)} of ${formatVisibleCount(total)} evidence records in this report scope have a recorded review status. ${formatVisibleCount(confirmedRisk)} require corrective action confirmation, ${formatVisibleCount(uncertainRisk)} remain evidence-limited, and ${formatVisibleCount(excludedRisk)} were reviewed out of report scope.`;
  }, [reviewOutcomeRollup, reviewOutcomeRollupLoading, safetyReportMvpRollupActive]);

  const customerReviewTrendSummary = useMemo(() => {
    if (safetyReportMvpRollupActive) return '';
    if (reviewOutcomeHistoryLoading) return 'Review-intelligence trend for this report scope is still being assembled.';
    const history = Array.isArray(reviewOutcomeHistory?.history)
      ? ((reviewOutcomeHistory?.history ?? []) as any[])
      : [];
    if (history.length <= 0) return '';
    const countDisposition = (bucket: any, disposition: string) =>
      Number(
        Array.isArray(bucket?.risk_disposition_items)
          ? ((bucket.risk_disposition_items.find((item: any) => String(item?.risk_disposition || '') === disposition)?.count) || 0)
          : 0,
      );
    const latest = history[history.length - 1];
    const previous = history.length > 1 ? history[history.length - 2] : null;
    const latestConfirmed = Number(latest?.customer_risk_total || 0);
    const latestUncertain = countDisposition(latest, 'uncertain_risk');
    const latestExcluded = countDisposition(latest, 'excluded_from_risk');
    if (!previous) {
      return `Latest review bucket shows ${formatVisibleCount(latestConfirmed)} confirmed safety concern${latestConfirmed === 1 ? '' : 's'}, ${formatVisibleCount(latestUncertain)} evidence-limited review${latestUncertain === 1 ? '' : 's'}, and ${formatVisibleCount(latestExcluded)} reviewed non-risk activit${latestExcluded === 1 ? 'y' : 'ies'}.`;
    }
    const prevConfirmed = Number(previous?.customer_risk_total || 0);
    const prevUncertain = countDisposition(previous, 'uncertain_risk');
    const prevExcluded = countDisposition(previous, 'excluded_from_risk');
    return `Trend: confirmed safety concerns moved from ${formatVisibleCount(prevConfirmed)} to ${formatVisibleCount(latestConfirmed)}; evidence-limited review moved from ${formatVisibleCount(prevUncertain)} to ${formatVisibleCount(latestUncertain)}; reviewed non-risk activity moved from ${formatVisibleCount(prevExcluded)} to ${formatVisibleCount(latestExcluded)}.`;
  }, [reviewOutcomeHistory, reviewOutcomeHistoryLoading, safetyReportMvpRollupActive]);
  const customerReviewBoundarySummary = useMemo(() => {
    if (safetyReportMvpRollupActive) return '';
    if (reviewOutcomeRollupLoading) return '';
    return buildScopeBoundarySummary(
      reviewOutcomeRollup,
      'evidence records in this report scope',
    );
  }, [reviewOutcomeRollup, reviewOutcomeRollupLoading, safetyReportMvpRollupActive]);

  const customerOwnershipRows = useMemo(
    () => (
      Array.isArray(ownershipRollup?.items)
        ? (ownershipRollup?.items ?? []).filter((item: any) => Number(item?.count || 0) > 0).slice(0, 4)
        : []
    ),
    [ownershipRollup],
  );

  const customerOwnershipDetailFacts = useMemo(
    () => [
      { label: displaySafetyManagerLabel('pattern_owner'), value: formatSafetyReportOwnerLabel(customerPatternOwnerDisplayLabel) },
      { label: displaySafetyManagerLabel('followup_owner'), value: formatSafetyReportOwnerLabel(customerFollowUpOwnerDisplayLabel) },
    ],
    [customerFollowUpOwnerDisplayLabel, customerPatternOwnerDisplayLabel],
  );

  const customerOwnershipSummary = useMemo(() => {
    if (safetyReportMvpRollupActive) {
      return '';
    }
    if (ownershipRollupLoading) return 'Ownership status for this report scope is still being assembled.';
    const total = Number(ownershipRollup?.total_incidents || 0);
    const assigned = Number(ownershipRollup?.assigned_total || 0);
    const unassigned = Number(ownershipRollup?.unassigned_total || 0);
    if (total <= 0) return 'No evidence records are in scope for this report window yet.';
    if (assigned <= 0) return `${formatVisibleCount(total)} evidence records are in scope and all still need an owner.`;
    if (unassigned <= 0) return `${formatVisibleCount(assigned)} evidence records in this report scope already have an owner.`;
    return `${formatVisibleCount(assigned)} of ${formatVisibleCount(total)} evidence records in this report scope have an owner; ${formatVisibleCount(unassigned)} still need assignment.`;
  }, [ownershipRollup, ownershipRollupLoading, safetyReportMvpRollupActive]);

  const customerEscalationRows = useMemo(
    () => (
      Array.isArray(escalationRollup?.items)
        ? (escalationRollup?.items ?? []).filter((item: any) => Number(item?.count || 0) > 0).slice(0, 4)
        : []
    ),
    [escalationRollup],
  );

  const customerEscalationSummary = useMemo(() => {
    if (safetyReportMvpRollupActive) return SAFETY_REPORT_MVP_REPORT_SCOPE.escalation;
    if (escalationRollupLoading) return 'Escalation status for this report scope is still being assembled.';
    const total = Number(escalationRollup?.total_incidents || 0);
    const escalated = Number(escalationRollup?.escalated_total || 0);
    const overdue = Number(escalationRollup?.overdue_total || 0);
    const scheduled = Number(escalationRollup?.scheduled_total || 0);
    if (total <= 0) return 'No evidence records are in scope for this report window yet.';
    if (escalated <= 0 && scheduled <= 0) return `${formatVisibleCount(total)} evidence records are in scope and none have active escalation pressure yet.`;
    if (overdue > 0) return `${formatVisibleCount(escalated)} evidence records in this report scope need escalation attention; ${formatVisibleCount(overdue)} are already overdue.`;
    if (scheduled > 0 && escalated <= 0) return `${formatVisibleCount(scheduled)} evidence records in this report scope have follow-up scheduled and no active escalation yet.`;
    return `${formatVisibleCount(escalated)} evidence records in this report scope need escalation attention.`;
  }, [escalationRollup, escalationRollupLoading, safetyReportMvpRollupActive]);

  const customerCorrectiveActionRows = useMemo(
    () => (
      Array.isArray(correctiveActionRollup?.items)
        ? (correctiveActionRollup?.items ?? []).filter((item: any) => Number(item?.count || 0) > 0).slice(0, 4)
        : []
    ),
    [correctiveActionRollup],
  );

  const customerCorrectiveActionSummary = useMemo(() => {
    if (safetyReportMvpRollupActive) return SAFETY_REPORT_MVP_REPORT_SCOPE.correctiveAction;
    if (correctiveActionRollupLoading) return 'Corrective-action status for this report scope is still being assembled.';
    const total = Number(correctiveActionRollup?.total_incidents || 0);
    const workOrders = Number(correctiveActionRollup?.work_order_total || 0);
    const notStarted = Number(correctiveActionRollup?.not_started_total || 0);
    if (total <= 0) return 'No evidence records are in scope for this report window yet.';
    if (workOrders <= 0) return `${formatVisibleCount(total)} evidence records are in scope and none are linked to a corrective work order yet.`;
    if (notStarted <= 0) return `${formatVisibleCount(workOrders)} evidence records in this report scope already have linked corrective work.`;
    return `${formatVisibleCount(workOrders)} of ${formatVisibleCount(total)} evidence records in this report scope are linked to corrective work; ${formatVisibleCount(notStarted)} still have no work order.`;
  }, [correctiveActionRollup, correctiveActionRollupLoading, safetyReportMvpRollupActive]);

  const customerMeasurementRows = useMemo(
    () => (
      Array.isArray(measurementRollup?.items)
        ? (measurementRollup?.items ?? []).filter((item: any) => Number(item?.count || 0) > 0).slice(0, 4)
        : []
    ),
    [measurementRollup],
  );

  const customerMeasurementSummary = useMemo(() => {
    if (safetyReportMvpRollupActive) return SAFETY_REPORT_MVP_REPORT_SCOPE.measurement;
    if (measurementRollupLoading) return 'Impact-measurement status for this report scope is still being assembled.';
    const total = Number(measurementRollup?.total_incidents || 0);
    const applicable = Number(measurementRollup?.applicable_total || 0);
    const ready = Number(measurementRollup?.ready_total || 0);
    const measured = Number(measurementRollup?.measured_total || 0);
    if (total <= 0) return 'No evidence records are in scope for this report window yet.';
    if (applicable <= 0) return `${formatVisibleCount(total)} evidence records are in scope, but none are ready to be measured yet.`;
    if (measured > 0) return `${formatVisibleCount(measured)} evidence records in this report scope already have recorded impact measurement.`;
    if (ready > 0) return `${formatVisibleCount(ready)} evidence records in this report scope are ready for impact measurement once the next window is recorded.`;
    return `${formatVisibleCount(applicable)} evidence records are on the measurement path, but review or corrective closure still blocks impact measurement.`;
  }, [measurementRollup, measurementRollupLoading, safetyReportMvpRollupActive]);
  const customerMeasurementBoundarySummary = useMemo(() => {
    if (safetyReportMvpRollupActive) return '';
    if (measurementRollupLoading) return '';
    return buildOutcomeBoundarySummary(
      measurementRollup,
      Number(measurementRollup?.measured_total || 0),
      'this report scope',
    );
  }, [measurementRollup, measurementRollupLoading, safetyReportMvpRollupActive]);

  const customerActionSnapshotRows = useMemo(
    () => (
      Array.isArray(actionSnapshotRollup?.items)
        ? (actionSnapshotRollup?.items ?? []).filter((item: any) => Number(item?.count || 0) > 0).slice(0, 4)
        : []
    ),
    [actionSnapshotRollup],
  );

  const customerActionSnapshotSummary = useMemo(() => {
    if (safetyReportMvpRollupActive) return SAFETY_REPORT_MVP_ACTION_SNAPSHOT;
    if (actionSnapshotRollupLoading) return 'Action workflow for this report scope is still being assembled.';
    const total = Number(actionSnapshotRollup?.total_incidents || 0);
    const inProgress = Number(actionSnapshotRollup?.in_progress_total || 0);
    const scheduled = Number(actionSnapshotRollup?.scheduled_total || 0);
    const closed = Number(actionSnapshotRollup?.closed_total || 0);
    if (total <= 0) return 'No evidence records are in scope for this report window yet.';
    if (closed > 0) return `${formatVisibleCount(closed)} evidence records in this report scope have completed the action loop.`;
    if (scheduled > 0) return `${formatVisibleCount(scheduled)} evidence records in this report scope already have a next review time recorded.`;
    if (inProgress > 0) return `${formatVisibleCount(inProgress)} evidence records in this report scope have action activity, but no follow-up time is recorded yet.`;
    return `${formatVisibleCount(total)} representative evidence sample${total === 1 ? '' : 's'} in this report scope still have no captured action owner, work order, or next review time yet.`;
  }, [actionSnapshotRollup, actionSnapshotRollupLoading, safetyReportMvpRollupActive]);

  const customerFollowupFacts = useMemo(() => {
    const projectedOwner = String(
      reportProjectionWorkflowState?.ownership?.followup_owner
      || reportProjectionWorkflowState?.ownership?.owner
      || '',
    ).trim();
    if (
      safetyReportMvpRollupActive
      && !projectedOwner
      && !customerOwnerLabel
      && customerPatternOwnerLabel === 'Not assigned'
      && customerFollowUpOwnerLabel === 'Not assigned'
    ) {
      return buildSafetyReportFollowupFacts({
        patternOwnerLabel: SAFETY_REPORT_MVP_FOLLOWUP.patternOwner,
        followupOwnerLabel: SAFETY_REPORT_MVP_FOLLOWUP.followUpOwner,
        currentActionLabel: SAFETY_REPORT_MVP_FOLLOWUP.currentAction,
        workflowDateLabel: SAFETY_REPORT_MVP_FOLLOWUP.workflowDate,
      });
    }
    const followUpOwner = (() => {
      const assignedTeam = String(reportProjectionWorkflowState?.ownership?.assigned_team || reportProjectionWorkflowState?.ownership?.assignment_team || '').trim();
      const owner = customerFollowUpOwnerDisplayLabel || customerOwnerLabel || 'Not assigned';
      if (assignedTeam && owner && owner !== assignedTeam && owner !== 'Not assigned' && owner !== 'Resolving ownership...') {
        return `${owner} (${assignedTeam})`;
      }
      return owner;
    })();
    return buildSafetyReportFollowupFacts({
      patternOwnerLabel: customerPatternOwnerDisplayLabel || 'Not assigned',
      followupOwnerLabel: followUpOwner,
      currentActionLabel:
        projectionCurrentActionLabel
        || safetyActionsForUi[0]?.label
        || operationalFollowThroughModel.nextBestAction.label
        || 'Corrective workflow pending',
      workflowDateLabel:
        formatTimestamp(String(reportProjectionWorkflowState?.outcome_measurement?.measured_at || '').trim())
        || formatTimestamp(String(reportProjectionWorkflowState?.action_snapshot?.next_review_at || '').trim())
        || formatTimestamp(supportPackageGeneratedAt)
        || operationalFollowThroughModel.summaryLine
        || 'Next review date not recorded',
      ruleOwnerLabel: customerRuleOwnerLabel,
    });
  }, [
    customerFollowUpOwnerDisplayLabel,
    customerFollowUpOwnerLabel,
    customerOwnerLabel,
    customerPatternOwnerDisplayLabel,
    customerPatternOwnerLabel,
    customerRuleOwnerLabel,
    operationalFollowThroughModel.nextBestAction.label,
    operationalFollowThroughModel.summaryLine,
    projectionCurrentActionLabel,
    reportProjectionWorkflowState,
    safetyActionsForUi,
    safetyReportMvpRollupActive,
    supportPackageGeneratedAt,
  ]);

  const customerUnprovenSummary = useMemo(() => {
    const projected = remapLegacyCustomerFacingCopy(String(reportProjectionCustomerSummary?.limitation_text || '').trim());
    if (projected) return projected;
    if (safetyReportMvpRollupActive && !isLoopedDemoProofCase) {
      return SAFETY_REPORT_MVP_UNPROVEN;
    }
    return buildCustomerUnprovenSummary({
      isLoopedDemoProofCase,
      demoOutcomeNote: currentResolutionProofCase.demoOutcomeNote,
      outcomeRecorded,
      evidenceReadinessLabel: String(reportProjectionEvidenceBreakdown?.readiness_label || '').trim(),
    });
  }, [
    currentResolutionProofCase.demoOutcomeNote,
    isLoopedDemoProofCase,
    outcomeRecorded,
    reportProjectionCustomerSummary,
    reportProjectionEvidenceBreakdown,
    safetyReportMvpRollupActive,
  ]);

  const proofReviewCorrelationLine = useMemo(() => {
    if (includedIncidentCaseCount && includedIncidentCaseCount > 1) {
      return 'This scan scope includes the anchor review incident plus related review incidents under the same safety rule, area, and selected period.';
    }
    return 'This scan scope is linked to the same review incident currently open on the main page.';
  }, [includedIncidentCaseCount]);

  const packageExportSummary = useMemo(() => {
    if (evidenceBundleReady) return 'Detailed supporting material stays in Advanced Technical Details. Most customers should use the main Safety Scan only.';
    if (evidenceBundleCreating) return 'Advanced Technical Details become more useful after the main Safety Scan finishes building.';
    if (evidenceBundleFailed) return 'Advanced Technical Details are less useful until the Safety Scan is rebuilt.';
    return 'Keep Advanced Technical Details secondary. Use them only when deeper supporting detail is needed.';
  }, [evidenceBundleCreating, evidenceBundleFailed, evidenceBundleReady]);

  const visibleProofScopeFacts = useMemo(
    () => proofScopeFacts.filter((fact) => {
      const value = String(fact.value || '').trim();
      return Boolean(value) && value !== 'Not captured yet' && value !== 'Unavailable';
    }),
    [proofScopeFacts],
  );

  const appliedProofScopeSummary = useMemo(() => {
    if (!hasAppliedIncidentSelection && !proofPackExists) {
      return 'No scan scope has been confirmed yet. Open from Review, then confirm the safety rule, area, and selected period before rebuilding the Safety Scan.';
    }
    const policy = customerFacingSafetyRuleLabel(currentResolutionProofCase.pattern || 'current safety rule');
    const rawZone = currentResolutionProofCase.zone || 'current area';
    const zone = customerFacingAreaLabel(rawZone) || rawZone;
    const windowLabel = currentResolutionProofCase.windowLabel || 'current selected period';
    const cases = includedIncidentCaseCount != null
      ? `${includedIncidentCaseCount} representative evidence sample${includedIncidentCaseCount === 1 ? '' : 's'}`
      : 'representative evidence samples not counted yet';
    const alerts = caseTotalIncidents ? `${caseTotalIncidents} alerts/events in the selected period` : 'alert/event count not captured yet';
    const anchor = anchorIncidentCaseLabel ? ` Review anchor: ${anchorIncidentCaseLabel}.` : '';
    return `${policy} in ${zone} for ${windowLabel}. Includes ${cases} and ${alerts}.${anchor}`;
  }, [anchorIncidentCaseLabel, caseTotalIncidents, currentResolutionProofCase, hasAppliedIncidentSelection, includedIncidentCaseCount, proofPackExists]);

  const technicalDetailsModel = useMemo<TechnicalDetailsModel>(() => {
    const formatProjectionWindow = (windowValue: any): string => {
      const from = String(windowValue?.from || '').trim();
      const to = String(windowValue?.to || '').trim();
      if (from || to) {
        return `${formatTimestamp(from) || 'Start time not set'} to ${formatTimestamp(to) || 'End time not set'}`;
      }
      const days = Number(windowValue?.days ?? NaN);
      if (Number.isFinite(days) && days > 0) return `Last ${days} day${days === 1 ? '' : 's'}`;
      return 'Not separately recorded';
    };
    const rawPolicy = policyTypeOptions.find((option) => option.value === proofScopePolicyTypeDraft)?.label
      || proofScopePolicyTypeDraft
      || String(reportProjectionLabelBundle?.customer_rule || '').trim()
      || currentResolutionProofCase.pattern
      || 'Not captured yet';
    const rawZone = zoneOptions.find((option) => option.value === proofScopeZoneDraft)?.label
      || proofScopeZoneDraft
      || String(reportProjectionLabelBundle?.customer_area || '').trim()
      || currentResolutionProofCase.zone
      || 'Not captured yet';
    const projectionWindowFrom = String(reportProjectionWindowContext?.selected_report_window?.from || '').trim();
    const projectionWindowTo = String(reportProjectionWindowContext?.selected_report_window?.to || '').trim();
    const incidentGroupDuration = formatProjectionWindow(reportProjectionWindowContext?.incident_group_duration);
    const recurrenceLookbackWindow = formatProjectionWindow(reportProjectionWindowContext?.recurrence_lookback_window);
    const evidenceCaptureWindow = formatProjectionWindow(reportProjectionWindowContext?.evidence_capture_window);
    const selectedPeriod = (
      projectionWindowFrom || projectionWindowTo
        ? `${formatTimestamp(projectionWindowFrom) || 'Start time not set'} to ${formatTimestamp(projectionWindowTo) || 'End time not set'}`
        : `${formatTimestamp(toIsoFromLocalInput(proofScopeFromDraft) || proofScopeFromDraft) || 'Start time not set'} to ${formatTimestamp(toIsoFromLocalInput(proofScopeToDraft) || proofScopeToDraft) || 'End time not set'}`
    );
    const claimCount = reportProjectionClaimList.length;
    const claimTypes = Array.from(new Set(reportProjectionClaimList.map((claim) => String(claim?.claim_type || '').trim()).filter(Boolean)));
    const claimTraceCount = Array.isArray(reportProjection?.technical_trace?.claim_trace) ? reportProjection?.technical_trace?.claim_trace?.length ?? 0 : 0;
    const projectionAnchorIncidentId = String(reportProjection?.anchor_incident_id || reportProjection?.technical_trace?.anchor_incident_id || '').trim();
    const projectionProofScopeId = String(reportProjection?.proof_scope_id || reportProjection?.technical_trace?.proof_scope_id || '').trim();
    const projectedSelectedIncidents = Number(
      reportProjectionReportCounts?.selected_incidents
      ?? reportProjectionWindowContext?.included_incident_cases
      ?? NaN,
    );
    const projectedClips = Number(reportProjectionEvidenceBreakdown?.accessible_clips || 0);
    const projectedSnapshots = Number(reportProjectionEvidenceBreakdown?.accessible_snapshots || 0);
    const projectedSourceContext = String(reportProjectionSourceContext?.source_type || reportProjectionSourceContext?.source_context || '').trim();
    const projectedReviewedBy = String(reportProjectionWorkflowState?.review?.reviewed_by || '').trim();
    const projectedReviewedAt = formatTimestamp(String(reportProjectionWorkflowState?.review?.reviewed_at || '').trim()) || 'Not recorded yet';
    const projectedRiskReductionStatus = String(reportProjectionWorkflowState?.outcome_measurement?.risk_reduction_status || '').trim();
    const projectedRiskReductionPercent = reportProjectionWorkflowState?.outcome_measurement?.risk_reduction_percent;
    const projectedReviewState = remapLegacyCustomerFacingCopy(
      String(reportProjectionWorkflowState?.review?.status_label || reportProjectionWorkflowState?.safety_case_state_label || '').trim(),
    );
    const projectedFollowupState = remapLegacyCustomerFacingCopy(
      String(reportProjectionWorkflowState?.outcome_measurement?.state_label || reportProjectionWorkflowState?.action_snapshot?.state_label || reportProjectionWorkflowState?.corrective_action?.state_label || '').trim(),
    );
    const projectedPatternSourceType = String(reportProjectionAnchorPatternSource?.source_type || '').trim();
    const projectedPatternGroupKey = String(reportProjectionAnchorPatternSource?.group_key || '').trim();
    const projectedPatternRepresentativeIncidentId = String(reportProjectionAnchorPatternSource?.representative_incident_id || '').trim();
    const projectedPatternRiskDisposition = String(reportProjectionPatternTrace?.risk_disposition || '').trim();
    return {
      title: SAFETY_REPORT_ADVANCED_DETAILS_TITLE,
      subtitle: 'Advanced details. Use this when you need deeper scope, traceability, or identifier-level detail.',
      backToProofPath: technicalDetailsBackPath,
      bundleSettings: {
        title: 'Scan setup',
        facts: [
          { label: 'Site', value: String(reportSiteId || canonicalProofSiteId || scopedSiteId || 'Not selected') },
          { label: 'Safety rule', value: customerFacingSafetyRuleLabel(
            String(incidentTypeHint || '').trim()
            || rawPolicy
            || customerFacingPatternLabel
            || 'Not captured yet',
          ) },
          { label: 'Policy ID', value: rawPolicy || 'Not captured yet' },
          { label: 'Area', value: customerFacingAreaLabel(rawZone) || rawZone },
          { label: 'Selected period', value: selectedPeriod },
          { label: 'Risk group duration', value: incidentGroupDuration },
          { label: 'Recurrence lookback', value: recurrenceLookbackWindow },
          { label: 'Evidence capture window', value: evidenceCaptureWindow },
          { label: 'Representative evidence samples in scope', value: Number.isFinite(projectedSelectedIncidents) && projectedSelectedIncidents > 0 ? formatVisibleCount(projectedSelectedIncidents) : (selectedIncidentCountForCustomer != null ? formatVisibleCount(selectedIncidentCountForCustomer) : 'Not captured yet') },
          { label: 'Proof scope ID', value: projectionProofScopeId || 'Not captured yet' },
        ],
      },
      evidenceControls: {
        title: 'Evidence diagnostics',
        facts: [
          { label: 'Safety Scan status', value: proofPageState === 'READY' ? 'Ready' : proofPageState === 'GENERATING' ? 'Building' : proofPageState === 'ERROR' ? 'Needs rebuild' : 'Needs review' },
          { label: 'Video clips', value: projectedClips > 0 ? `${formatVisibleCount(projectedClips)} available` : (clipReadyCount > 0 ? `${formatVisibleCount(clipReadyCount)} available` : clipPendingCount > 0 ? `${formatVisibleCount(clipPendingCount)} pending` : 'Not captured yet') },
          { label: 'Snapshots', value: projectedSnapshots > 0 ? `${formatVisibleCount(projectedSnapshots)} available` : (snapshotReadyCount > 0 ? `${formatVisibleCount(snapshotReadyCount)} available` : 'Not captured yet') },
          { label: 'Latest event', value: latestEventTimestampLabel || 'Not captured yet' },
          { label: displaySafetyManagerLabel('followup_owner'), value: customerOwnerLabel || 'Not assigned' },
          { label: `${displaySafetyManagerLabel('pattern_owner')} (rule config)`, value: customerRuleOwnerLabel || 'Not assigned' },
          { label: 'Report claims', value: claimCount > 0 ? `${formatVisibleCount(claimCount)} emitted` : 'Not captured yet' },
        ],
      },
      advancedDiagnostics: {
        title: 'System diagnostics',
        facts: [
          { label: 'Review state', value: projectedReviewState || customerStatusLabel || 'Needs review' },
          { label: 'Reviewed by', value: projectedReviewedBy || 'Not recorded yet' },
          { label: 'Reviewed at', value: projectedReviewedAt },
          { label: 'Action state', value: projectedFollowupState || customerFollowUpFact || 'Not confirmed' },
          { label: 'Risk reduction status', value: projectedRiskReductionStatus || 'Not measured' },
          { label: 'Risk reduction percent', value: projectedRiskReductionPercent != null ? `${projectedRiskReductionPercent}%` : 'Not measured' },
          { label: 'Evidence confidence', value: evidenceConfidenceSummary || 'Unavailable' },
          { label: 'Pipeline status', value: evidenceBundleFailed ? 'Needs attention' : 'OK' },
          { label: 'Source context', value: projectedSourceContext || 'Unavailable' },
          { label: 'Risk source', value: projectedPatternSourceType || 'Not captured yet' },
          { label: 'Risk group key', value: projectedPatternGroupKey || 'Not captured yet' },
          { label: 'Risk representative incident', value: projectedPatternRepresentativeIncidentId || 'Not captured yet' },
          { label: 'Risk disposition', value: projectedPatternRiskDisposition || 'Not captured yet' },
          { label: 'Appendix summary', value: packageExportSummary },
          { label: 'Claim types', value: claimTypes.length ? claimTypes.join(', ') : 'Not captured yet' },
        ],
      },
      rawDetails: {
        title: 'Raw identifiers',
        facts: [
          { label: 'Review anchor ID', value: projectionAnchorIncidentId || anchorIncidentCaseLabel || 'Not captured yet' },
          { label: 'Area ID', value: String(reportZoneId || canonicalProofZoneId || 'Not captured yet') },
          { label: 'Camera/source IDs', value: [filterCameraId || null, reportDeviceId || canonicalProofDeviceId || null].filter(Boolean).join(' / ') || 'Not captured yet' },
          { label: 'Export timestamp', value: formatTimestamp(supportPackageGeneratedAt) || 'Not captured yet' },
          { label: 'Claim trace entries', value: claimTraceCount > 0 ? String(claimTraceCount) : 'Not captured yet' },
        ],
      },
    };
  }, [
    anchorIncidentCaseLabel,
    canonicalProofDeviceId,
    canonicalProofSiteId,
    canonicalProofZoneId,
    clipPendingCount,
    clipReadyCount,
    currentResolutionProofCase.pattern,
    currentResolutionProofCase.zone,
    customerFacingPatternLabel,
    incidentTypeHint,
    customerFollowUpFact,
    customerOwnerLabel,
    customerRuleOwnerLabel,
    customerStatusLabel,
    evidenceBundleFailed,
    filterCameraId,
    latestEventTimestampLabel,
    packageExportSummary,
    policyTypeOptions,
    proofPageState,
    proofScopeFromDraft,
    proofScopePolicyTypeDraft,
    proofScopeToDraft,
    proofScopeZoneDraft,
    reportDeviceId,
    reportProjection,
    reportProjectionAnchorPatternSource,
    reportProjectionClaimList,
    reportProjectionEvidenceBreakdown,
    reportProjectionLabelBundle,
    reportProjectionPatternTrace,
    reportProjectionReportCounts,
    reportProjectionSourceContext,
    reportProjectionWindowContext,
    reportProjectionWorkflowState,
    reportSiteId,
    reportZoneId,
    scopedSiteId,
    selectedIncidentCountForCustomer,
    snapshotReadyCount,
    supportPackageGeneratedAt,
    technicalDetailsBackPath,
    zoneOptions,
    evidenceConfidenceSummary,
  ]);

  return {
    reportProjection,
    reportProjectionClaimList,
    reportProjectionCustomerSummary,
    reportProjectionWorkflowState,
    reportProjectionEvidenceBreakdown,
    reportProjectionReportCounts,
    reportProjectionWindowContext,
    reportProjectionLabelBundle,
    reportProjectionSourceContext,
    investigationAlignment,
    claimsRoiAlignment,
    safetyControlReview,
    customerOwnerLabel,
    selectedIncidentCountForCustomer,
    relatedAlertEventCount,
    latestEventTimestampLabel,
    snapshotReadyCount,
    clipReadyCount,
    clipPendingCount,
    customerAreaLabel,
    customerSafetyRuleLabel,
    customerIncidentLabel,
    projectionFollowupFact,
    projectionCurrentActionLabel,
    customerSummarySentence,
    customerStatusLabel,
    customerEvidenceFact,
    customerFollowUpFact,
    customerTopStatusPanel,
    customerSecondaryStatusFacts,
    customerWhatHappened,
    visibleRiskPatternSummary,
    visibleRiskPatternTopHotspotLabel,
    visibleRiskPatternDominantLabel,
    customerRiskPatternSummary,
    customerRiskPatternFacts,
    customerEvidencePoints,
    customerReviewStatusSummary,
    customerReviewChecklist,
    customerReviewOutcomeRows,
    customerReviewOutcomeSummary,
    customerReviewTrendSummary,
    customerReviewBoundarySummary,
    customerOwnershipDetailFacts,
    customerOwnershipRows,
    customerOwnershipSummary,
    customerEscalationRows,
    customerEscalationSummary,
    customerCorrectiveActionRows,
    customerCorrectiveActionSummary,
    customerMeasurementRows,
    customerMeasurementSummary,
    customerMeasurementBoundarySummary,
    customerActionSnapshotRows,
    customerActionSnapshotSummary,
    customerFollowupFacts,
    customerUnprovenSummary,
    safetyReportReadinessState,
    safetyReportReadinessLabel,
    safetyWorkflowProgress,
    proofReviewCorrelationLine,
    packageExportSummary,
    visibleProofScopeFacts,
    appliedProofScopeSummary,
    technicalDetailsModel,
  };
}
